/** Типы объектов API (docs/api/openapi.yaml). Snowflake передаются строками. */
export type Snowflake = string

export type PresenceStatus = 'online' | 'idle' | 'dnd' | 'offline' | 'invisible'
/** Статус, который человек выбирает сам: «не в сети» выбрать нельзя — для этого есть «невидимый». */
export type SelfStatus = 'online' | 'idle' | 'dnd' | 'invisible'

/** Статус и свой статус: GET/PATCH /users/@me/settings, user_settings в READY, USER_SETTINGS_UPDATE. */
export interface UserSettings {
  status: SelfStatus
  /** expires_at — когда свой статус уберётся сам (RFC 3339); нет — не уберётся. */
  custom_status: { text: string; expires_at?: string | null } | null
  /** Показывать ли запущенную игру; нет поля — старый сервер, считаем, что показывать. */
  show_current_game?: boolean
  theme?: string
  locale?: string
}

export interface User {
  id: Snowflake
  username: string
  global_name: string | null
  avatar: string | null
  bot?: boolean
  /** Рамка аватара; нет поля или null — без рамки (lib/decor.ts). */
  avatar_decoration_data?: AvatarDecoration | null
  /** Стиль ника; нет поля или null — обычный. */
  display_name_styles?: NameStyle | null
  /** Тег сервера рядом с ником (как primary_guild у Discord); null — без тега. */
  primary_guild?: PrimaryGuild | null
  /** Табличка — фон строки в списке участников (как collectibles у Discord). */
  collectibles?: { nameplate: Nameplate | null } | null
}

/** Табличка: asset — имя картинки в /decorations (plate-<asset>.webp), label — название. */
export interface Nameplate {
  sku_id: Snowflake
  asset: string
  label: string
  palette: string
}

/** Чей тег человек носит: сервер, текст тега и его цвет 0xRRGGBB. */
export interface PrimaryGuild {
  identity_guild_id: Snowflake
  identity_enabled: boolean
  tag: string
  badge: string | null
  color: number
}

/** Рамка аватара, как avatar_decoration_data у Discord: asset — имя картинки в /decorations. */
export interface AvatarDecoration {
  asset: string
  sku_id: Snowflake
}

/** Стиль ника: шрифт, эффект и до двух цветов 0xRRGGBB. Номера — lib/decor.ts. */
export interface NameStyle {
  font_id: number
  effect_id: number
  colors: number[]
}

/** Эффект карточки профиля: id — имя картинки в /decorations. */
export interface ProfileEffect {
  id: string
}

/** Карточка профиля: GET /users/{id} — пользователь плюс то, что видно только в карточке. */
export interface Profile extends User {
  banner: string | null
  bio: string
  created_at: string
  premium: boolean
  /** Эффект карточки профиля; нет поля или null — без эффекта. */
  profile_effect?: ProfileEffect | null
  /** Цвета профиля [основной, акцент] 0xRRGGBB — рамка и фон карточки; null — обычные. */
  theme_colors?: number[] | null
  /** Значки: id — какой рисовать, name — подпись. */
  badges?: { id: string; name: string }[]
  /** Витрина: до трёх достижений. */
  achievements_showcase?: { id: string; name: string; description: string }[]
  /** Серверы, где состоим оба: идентификаторы — остальное клиент знает сам. */
  mutual_guild_ids: Snowflake[]
  mutual_friends: User[]
  /** Вкладка «Активность»: во что играл, свежие сначала. Чужим — только если игра не скрыта. */
  games?: GamePlayed[]
}

/** Игра из истории «Активность». */
export interface GamePlayed {
  name: string
  /** Номер в Steam — по нему обложка. */
  steam_app_id?: string
  first_played_at: string
  last_played_at: string
  /** В скольких разных днях играл. */
  days: number
  /** Дней подряд по сегодня или вчера; 0 — серия прервалась. */
  streak: number
}

export interface Presence {
  status: PresenceStatus
  activity?: string | null
}

/**
 * Активность в присутствии с шлюза (форма Discord): 0 играет, 1 стримит, 2 слушает, 3 смотрит,
 * 4 свой статус (текст в state), 5 соревнуется. steam_app_id — наше поле: обложка игры.
 */
export interface PresenceActivity {
  name: string
  type: number
  state?: string
  url?: string
  steam_app_id?: string
}

export interface Role {
  id: Snowflake
  name: string
  color: number // RGB числом, 0 — без цвета
  hoist: boolean
  position: number
  permissions: string // 64-битная маска строкой
  managed: boolean
  mentionable: boolean
  icon: string | null
}

export interface Member {
  user: User
  guild_id?: Snowflake
  nick: string | null
  avatar: string | null
  roles: Snowflake[]
  joined_at: string
  premium_since: string | null
  deaf: boolean
  mute: boolean
  pending: boolean
  communication_disabled_until: string | null
  permissions?: string
}

export interface Guild {
  id: Snowflake
  name: string
  icon: string | null
  banner?: string | null
  description?: string | null
  owner_id: Snowflake
  verification_level: number
  default_message_notifications: number
  system_channel_id: Snowflake | null
  rules_channel_id: Snowflake | null
  afk_channel_id: Snowflake | null
  afk_timeout: number
  premium_tier: number
  premium_subscription_count: number
  /** Полоска бустов над каналами: сколько до следующего уровня. */
  premium_progress_bar_enabled?: boolean
  features: string[]
  roles: Role[]
  emojis: Emoji[]
  stickers?: Sticker[]
  /** Звуки звуковой панели сервера (READY, GUILD_CREATE), без авторов. */
  soundboard_sounds?: SoundboardSound[]
  approximate_member_count?: number
  approximate_presence_count?: number
  /** Тег сервера (2–4 буквы), который участники носят рядом с ником, и его цвет; null — нет. */
  tag?: string | null
  tag_color?: number | null
  /** Только в READY / GUILD_CREATE. */
  joined_at?: string
  member_count?: number
  channels?: Channel[]
  /** Активные ветки (READY, GUILD_CREATE). */
  threads?: Channel[]
  members?: Member[]
  presences?: PresenceUpdate[]
  voice_states?: VoiceState[]
}

export interface GuildPartial {
  id: Snowflake
  name: string
  icon: string | null
  owner: boolean
  permissions: string
  features: string[]
}

export interface Overwrite {
  id: Snowflake
  type: 0 | 1 // 0 — роль, 1 — участник
  allow: string
  deny: string
}

export interface Invite {
  code: string
  guild: { id: Snowflake; name: string; icon: string | null; description: string | null; features: string[] }
  channel: { id: Snowflake; name: string; type: number }
  inviter?: User
  approximate_member_count: number
  approximate_presence_count: number
  expires_at: string | null
  uses: number
  max_uses: number
  max_age: number
  temporary: boolean
  created_at: string
}

export interface Ban {
  user: User
  reason: string | null
}

export interface MuteConfig {
  end_time: string | null
}

export interface ChannelOverride {
  channel_id: Snowflake
  message_notifications: number // 0 все, 1 упоминания, 2 ничего, 3 как на сервере
  muted: boolean
  mute_config: MuteConfig | null
  collapsed: boolean
}

/** Настройки уведомлений пользователя для сервера (user_guild_settings); guild_id null — личные беседы. */
export interface UserGuildSettings {
  guild_id: Snowflake | null
  message_notifications: number
  muted: boolean
  mute_config: MuteConfig | null
  suppress_everyone: boolean
  suppress_roles: boolean
  channel_overrides: ChannelOverride[]
}

export interface AuditLogChange {
  key: string
  old_value?: unknown
  new_value?: unknown
}

export interface AuditLogEntry {
  id: Snowflake
  guild_id?: Snowflake
  action_type: number
  user_id: Snowflake | null
  target_id: Snowflake | null
  reason?: string
  changes: AuditLogChange[]
  options: Record<string, unknown>
  created_at: string
}

export interface AuditLog {
  audit_log_entries: AuditLogEntry[]
  users: User[]
}

/** Типы каналов совпадают с Discord (спецификация 6.2). */
export const ChannelType = {
  GuildText: 0,
  DM: 1,
  GuildVoice: 2,
  GroupDM: 3,
  GuildCategory: 4,
  GuildAnnouncement: 5,
  PublicThread: 11,
  GuildStageVoice: 13,
  GuildForum: 15,
} as const
export type ChannelType = (typeof ChannelType)[keyof typeof ChannelType]

export interface ForumTag { id: Snowflake; name: string }

export interface Channel {
 available_tags?: ForumTag[]
 applied_tags?: Snowflake[]
 forum_pinned?: boolean
  id: Snowflake
  type: ChannelType
  guild_id?: Snowflake
  name: string | null
  topic?: string | null
  position?: number
  parent_id?: Snowflake | null
  nsfw?: boolean
  rate_limit_per_user?: number
  bitrate?: number
  user_limit?: number
  permission_overwrites?: Overwrite[]
  last_message_id: Snowflake | null
  recipients?: User[]
  owner_id?: Snowflake
  icon?: string | null
  flags?: number
  /** Только у веток (тип 11). */
  thread_metadata?: ThreadMetadata
  message_count?: number
  member_count?: number
  /** Участие текущего пользователя в ветке. */
  member?: ThreadMember | null
  last_pin_timestamp?: string | null
}

/** Голосовое состояние (VOICE_STATE_UPDATE): channel_id null — вышел. */
export interface VoiceState {
  /** Temporary channel restriction, independent of personal and moderator mute/deaf. */
  afk?: boolean
  guild_id?: Snowflake
  channel_id: Snowflake | null
  user_id: Snowflake
  member?: Member
  session_id: string
  deaf: boolean
  mute: boolean
  self_deaf: boolean
  self_mute: boolean
  self_stream: boolean
  self_video: boolean
  suppress: boolean
}

/** Звонок в ЛС или группе: CALL_CREATE/CALL_UPDATE и READY.calls (voice_states — в CALL_CREATE и READY). */
export interface Call {
  channel_id: Snowflake
  message_id: Snowflake
  region: string
  ringing: Snowflake[]
  voice_states?: VoiceState[] | undefined
}

/** Ответ на вход в голосовой канал и тело VOICE_SERVER_UPDATE; guild_id null в звонке ЛС. */
export interface VoiceJoin {
  endpoint: string
  token: string
  region: string
  expires_at: string
  guild_id: Snowflake | null
  channel_id: Snowflake
  voice_state: VoiceState
}

/** Ленивый список участников (op 14 → GUILD_MEMBER_LIST_UPDATE). */
export interface MemberListGroup {
  id: string // id роли, 'online' или 'offline'
  count: number
}

export interface ListMember extends Member {
  presence: PresenceUpdate
}

export interface MemberListItem {
  group?: MemberListGroup
  member?: ListMember
}

export interface MemberListUpdate {
  guild_id: Snowflake
  id: string
  member_count: number
  online_count: number
  groups: MemberListGroup[]
  ops: { op: 'SYNC' | 'INVALIDATE'; range: [number, number]; items: MemberListItem[] }[]
}

export interface ThreadMetadata {
  archived: boolean
  auto_archive_duration: number
  archive_timestamp: string | null
  locked: boolean
  create_timestamp: string
}

export interface ThreadMember {
  id: Snowflake
  user_id: Snowflake
  join_timestamp: string
  flags: number
  user?: User
}

export interface Attachment {
  id: Snowflake
  filename: string
  description?: string
  size: number
  content_type: string
  url: string
  proxy_url?: string
  width?: number | null
  height?: number | null
  spoiler?: boolean
  /** Антивирус нашёл угрозу: файл удалён, ссылок нет. */
  blocked?: boolean
  /** Голосовое сообщение: длительность в секундах и волна — base64 до 256 значений громкости 0–255. */
  duration_secs?: number
  waveform?: string
}

/** Ссылка на загруженный по presigned URL файл в теле сообщения. */
export interface AttachmentRef {
  id: string
  filename: string
  uploaded_filename: string
  description?: string
  duration_secs?: number
  waveform?: string
}

export interface ReactionEmoji {
  id: Snowflake | null
  name: string
  animated?: boolean
}

export interface Reaction {
  emoji: ReactionEmoji
  count: number
  me: boolean
}

/** Эмодзи сервера; url — адрес картинки (расширение формы Discord). */
export interface Emoji {
  id: Snowflake
  name: string
  animated: boolean
  managed?: boolean
  available?: boolean
  require_colons?: boolean
  roles?: Snowflake[]
  user?: User
  url?: string
}

export interface EmbedMedia {
  url: string
  /** Подписанная ссылка через прокси сервера: IP пользователя внешнему сайту не виден. */
  proxy_url?: string
  width?: number
  height?: number
}

/** Превью ссылки (строит worker) или карточка бота — форма Discord. */
export interface Embed {
  type?: 'rich' | 'image' | 'video' | 'link' | 'article'
  title?: string
  description?: string
  url?: string
  timestamp?: string
  color?: number
  footer?: { text: string; icon_url?: string }
  image?: EmbedMedia
  thumbnail?: EmbedMedia
  /** Только у YouTube, RuTube и VK Видео: адрес плеера для iframe. */
  video?: EmbedMedia
  provider?: { name?: string; url?: string }
  author?: { name: string; url?: string; icon_url?: string }
  /** Поля rich-карточек ботов и вебхуков. */
  fields?: { name: string; value: string; inline?: boolean }[]
}

// --- Импорт структуры сервера из Discord ------------------------------------------------

export type ImportStatus = 'uploaded' | 'ready' | 'running' | 'done' | 'failed' | 'cancelled'

export interface ImportChannelPreview {
  id: string
  name: string
  type: number
  target_type: number
  position: number
  topic?: string | null
  nsfw?: boolean
  overwrites: number
  warnings?: string[]
  skip?: string
}

export interface ImportPreview {
  guild: { name: string; description: string | null; verification_level: number; default_message_notifications: number; afk_timeout: number; has_icon: boolean; has_banner: boolean; exported_at: string; tool: string }
  summary: Record<string, number>
  categories: { id: string; name: string; position: number; channels: ImportChannelPreview[]; warnings?: string[] }[]
  uncategorized: ImportChannelPreview[]
  roles: { id: string; name: string; color: number; position: number; hoist: boolean; managed: boolean; everyone: boolean; permissions: string; warnings?: string[]; skip?: string }[]
  emojis: { id: string; name: string; animated?: boolean; size: number; skip?: string }[]
  stickers: { id: string; name: string; format_type?: number; size: number; skip?: string }[]
  threads: { id: string; name: string; parent_id: string; archived: boolean; private: boolean; warnings?: string[]; skip?: string }[]
  claims: number
  slots: Record<string, number>
  warnings: string[]
  dropped?: Record<string, number>
}

export interface ImportOptions {
  channels: string[]
  roles: string[]
  emojis: string[]
  stickers: string[]
  threads: string[]
  settings: boolean
  claims: boolean
  renames?: Record<string, string>
}

export interface ImportReport {
  created: Record<string, number>
  existing?: Record<string, number>
  skipped: { type: string; id: string; name: string; reason: string }[]
  warnings: string[]
  duration_ms: number
}

export interface ImportJob {
  id: Snowflake
  guild_id: Snowflake
  owner_id: Snowflake
  source: string
  status: ImportStatus
  options: ImportOptions | Record<string, never>
  preview?: ImportPreview
  report?: ImportReport
  error: string | null
  progress?: { step: string; done: number; total: number }
  created_at: string
  started_at: string | null
  finished_at: string | null
}

/** Стикер сервера (форма Discord, type 2); format_type: 1 PNG, 2 APNG, 4 GIF; url — расширение для клиента. */
export interface Sticker {
  id: Snowflake
  name: string
  description: string
  tags: string
  type: number
  format_type: number
  available?: boolean
  guild_id: Snowflake
  user?: User
  url?: string
}

/** Звук звуковой панели: стандартный (без guild_id) или звук сервера. Файл — GET /soundboard-sounds/{sound_id}. */
export interface SoundboardSound {
  sound_id: Snowflake
  name: string
  /** Громкость звука для всех, 0…1. */
  volume: number
  emoji_id: Snowflake | null
  emoji_name: string | null
  guild_id?: Snowflake
  available: boolean
  /** Кто загрузил — только в списке звуков сервера. */
  user?: User
}

/** VOICE_CHANNEL_EFFECT_SEND: кто и какой звук сыграл в голосовом канале. */
export interface VoiceChannelEffect {
  channel_id: Snowflake
  guild_id: Snowflake | null
  user_id: Snowflake
  sound_id: Snowflake
  sound_volume: number
  emoji: { id: Snowflake | null; name: string | null } | null
}

/** Стикер в сообщении (sticker_items). */
export interface StickerItem {
  id: Snowflake
  name: string
  format_type: number
}

/** Входящий вебхук (форма Discord); token и url видны тем, у кого MANAGE_WEBHOOKS. */
export interface Webhook {
  id: Snowflake
  type: number
  guild_id: Snowflake
  channel_id: Snowflake
  user?: User
  name: string
  avatar: string | null
  token?: string
  application_id: Snowflake | null
  url?: string
}

/** Флаги сообщения — как у Discord. */
export const MessageFlags = {
  SuppressEmbeds: 4,
  HasThread: 32,
  IsVoiceMessage: 8192,
  HasSnapshot: 16384,
  /** Ответ приложения только вызвавшему: в базу не пишется, живёт в памяти клиента. */
  Ephemeral: 64,
  /** Бот принял команду и ещё думает (отложенный ответ). */
  Loading: 128,
} as const

/** Копия пересланного сообщения (message_snapshots), как у Discord: содержимое без автора. */
export interface MessageSnapshot {
  message: {
    type: number
    content: string
    embeds: Embed[]
    attachments: Attachment[]
    timestamp: string
    edited_timestamp: string | null
    flags?: number
    mentions: User[]
    mention_roles?: Snowflake[]
    sticker_items?: StickerItem[]
  }
}

/** Типы сообщений — как у Discord; системные показываются иначе. */
export const MessageType = {
  Default: 0,
  RecipientAdd: 1,
  RecipientRemove: 2,
  Call: 3,
  ChannelNameChange: 4,
  ChannelPinned: 6,
  UserJoin: 7,
  ThreadCreated: 18,
  Reply: 19,
  PollResult: 46,
  /** Ответ приложения на слэш-команду. */
  ChatInputCommand: 20,
  ContextMenuCommand: 23,
} as const

/** Вопрос или вариант опроса: текст и эмодзи (сервера — id, обычный — name). */
export interface PollMedia {
  text?: string
  emoji?: { id: Snowflake | null; name: string | null } | null
}

export interface PollAnswerCount {
  id: number
  count: number
  me_voted: boolean
}

/** Опрос в сообщении, как у Discord. results.me_voted — свои голоса (в событиях шлюза всегда false). */
export interface Poll {
  question: PollMedia
  answers: { answer_id: number; poll_media: PollMedia }[]
  expiry: string | null
  allow_multiselect: boolean
  layout_type: number
  results?: { is_finalized: boolean; answer_counts: PollAnswerCount[] }
}

/** Опрос при отправке: duration — часы (1…768). */
export interface PollCreate {
  question: { text: string }
  answers: { poll_media: PollMedia }[]
  duration: number
  allow_multiselect: boolean
}

export interface Message {
  id: Snowflake
  channel_id: Snowflake
  guild_id?: Snowflake
  author: User
  member?: { nick: string | null; roles: Snowflake[] }
  content: string
  timestamp: string
  edited_timestamp: string | null
  mentions: User[]
  mention_roles?: Snowflake[]
  mention_everyone?: boolean
  attachments: Attachment[]
  embeds: Embed[]
  reactions: Reaction[]
  pinned: boolean
  type: number
  flags?: number
  /** type 1 — пересылка: канал и сервер источника, копия — в message_snapshots. */
  message_reference?: { type?: number; message_id: Snowflake; channel_id: Snowflake; guild_id?: Snowflake } | null
  message_snapshots?: MessageSnapshot[]
  referenced_message?: Message | null
  /** Ветка, начатая от этого сообщения (flags & 32). */
  thread?: Channel | null
  /** Звонок в ЛС (тип 3): участники и время окончания (null — идёт). */
  call?: { participants: Snowflake[]; ended_timestamp: string | null } | null
  nonce?: string | null
  /** Сообщение вебхука: author — вебхук с bot: true и именем из сообщения. */
  webhook_id?: Snowflake
  sticker_items?: StickerItem[]
  poll?: Poll
  /** Ответ приложения (ADR 0010): чьё. У сообщений бота, отправленных без взаимодействия, может не быть. */
  application_id?: Snowflake | null
  /** Ответ на взаимодействие: кто его вызвал и чем. */
  interaction_metadata?: MessageInteractionMetadata | null
  /** Старая форма того же — в ней есть имя команды (discord.js читает обе). */
  interaction?: MessageInteraction | null
  /** Кнопки и списки под сообщением: строки (type 1). */
  components?: ActionRow[]
  /** Только на клиенте: сообщение ещё отправляется или не отправилось. */
  pending?: boolean
  failed?: boolean
}

/** Связь с другим пользователем: 1 друг, 2 заблокирован, 3 входящая заявка, 4 исходящая. */
export interface Relationship {
  id: Snowflake
  type: 1 | 2 | 3 | 4
  user: User
  since: string
}

export interface ReadState {
  id: Snowflake
  last_message_id: Snowflake | null
  mention_count: number
}

export interface PresenceUpdate {
  user: { id: Snowflake }
  status: PresenceStatus
  activities: PresenceActivity[]
  client_status: Record<string, string>
}

export interface ReadyPayload {
  v: number
  session_id: string
  user: User & { mfa_enabled?: boolean; locale?: string }
  guilds: Guild[]
  user_guild_settings?: UserGuildSettings[]
  private_channels: Channel[]
  relationships: Relationship[]
  read_state: ReadState[]
  presences: PresenceUpdate[]
  /** Идущие звонки в ЛС пользователя. */
  calls?: Call[]
  user_settings: { theme: string; locale: string; status: string; custom_status?: { text: string; expires_at?: string | null } | null }
}

export interface ApiError {
  code: number
  message: string
  errors?: unknown
}

/** Автомод сервера (форма Discord): правило, срабатывание и действия. */
export const AutomodTrigger = { Keyword: 1, Spam: 3, KeywordPreset: 4, MentionSpam: 5 } as const
export const AutomodActionType = { BlockMessage: 1, SendAlert: 2, Timeout: 3 } as const

export interface AutomodTriggerMetadata {
  keyword_filter: string[]
  regex_patterns: string[]
  presets: number[]
  allow_list: string[]
  mention_total_limit?: number
  mention_raid_protection_enabled?: boolean
}

export interface AutomodAction {
  type: number
  metadata: { channel_id?: Snowflake | null; duration_seconds?: number; custom_message?: string }
}

export interface AutomodRule {
  id: Snowflake
  guild_id: Snowflake
  name: string
  creator_id: Snowflake
  event_type: number
  trigger_type: number
  trigger_metadata: AutomodTriggerMetadata
  actions: AutomodAction[]
  enabled: boolean
  exempt_roles: Snowflake[]
  exempt_channels: Snowflake[]
}

export type AutomodRuleInput = Partial<Pick<AutomodRule, 'name' | 'event_type' | 'trigger_type' | 'trigger_metadata' | 'actions' | 'enabled' | 'exempt_roles' | 'exempt_channels'>>

// --- Приложения и боты (спецификация 6.7) -----------------------------------------------

/** Приложение разработчика в форме Discord; privileged_intents и redirect_uris — расширения раздела «Приложения». */
export interface Application {
  id: Snowflake
  name: string
  icon: string | null
  description: string
  bot_public: boolean
  bot_require_code_grant: boolean
  bot?: User
  owner?: User
  verify_key: string
  team: null
  flags: number
  redirect_uris: string[]
  /** Биты привилегированных intents, разрешённые боту: 2 — участники, 256 — присутствие, 32768 — содержимое сообщений. */
  privileged_intents: number
  approximate_guild_count?: number
  created_at: string
}

/** Данные страницы /oauth2/authorize: что за бот и на какие серверы его можно добавить. */
export interface AuthorizeInfo {
  application: Application
  bot: User
  user: User
  guilds: GuildPartial[]
  scopes: string[]
  permissions: string
  guild_id: Snowflake | null
  authorized: boolean
}

// --- Слэш-команды, кнопки и списки в сообщениях (ADR 0010, формы Discord) ---------------

/** Тип команды: 1 — слэш-команда; 2 и 3 (меню по правому клику) сервер хранит, клиент пока не показывает. */
export const CommandType = { ChatInput: 1, User: 2, Message: 3 } as const

/** Типы опций команды — как у Discord. */
export const OptionType = {
  SubCommand: 1,
  SubCommandGroup: 2,
  String: 3,
  Integer: 4,
  Boolean: 5,
  User: 6,
  Channel: 7,
  Role: 8,
  Mentionable: 9,
  Number: 10,
  Attachment: 11,
} as const
export type OptionType = (typeof OptionType)[keyof typeof OptionType]

/** Вариант значения опции: из choices команды или из ответа автодополнения. */
export interface CommandChoice {
  name: string
  name_localizations?: Record<string, string> | null
  value: string | number
}

export interface CommandOption {
  type: OptionType
  name: string
  name_localizations?: Record<string, string> | null
  description: string
  description_localizations?: Record<string, string> | null
  required?: boolean
  choices?: CommandChoice[]
  /** У подкоманды и группы — вложенные опции. */
  options?: CommandOption[]
  channel_types?: number[]
  min_value?: number
  max_value?: number
  min_length?: number
  max_length?: number
  autocomplete?: boolean
}

export interface ApplicationCommand {
  id: Snowflake
  /** Нет поля — 1 (CHAT_INPUT), как у Discord. */
  type?: number
  application_id: Snowflake
  guild_id?: Snowflake | null
  name: string
  name_localizations?: Record<string, string> | null
  description: string
  description_localizations?: Record<string, string> | null
  options?: CommandOption[]
  default_member_permissions: string | null
  dm_permission?: boolean
  contexts?: number[] | null
  integration_types?: number[]
  nsfw?: boolean
  version: Snowflake
}

/** Приложение в списке команд: его имя и значок стоят над его командами. */
export interface CommandApplication {
  id: Snowflake
  name: string
  icon: string | null
  bot_id?: Snowflake | null
  /** Учётная запись бота, если сервер её прислал: по ней аватар. */
  bot?: User
}

/** GET /guilds/{id}/application-command-index и /channels/{id}/application-command-index. */
export interface ApplicationCommandIndex {
  applications: CommandApplication[]
  application_commands: ApplicationCommand[]
}

export const InteractionType = { Ping: 1, ApplicationCommand: 2, MessageComponent: 3, Autocomplete: 4, ModalSubmit: 5 } as const

/** Значение опции в запросе на выполнение команды; у подкоманды — вложенные options. */
export interface InteractionDataOption {
  name: string
  type: number
  value?: string | number | boolean
  options?: InteractionDataOption[]
  /** Автодополнение: поле, которое сейчас набирают. */
  focused?: boolean
}

export const ComponentType = {
  ActionRow: 1,
  Button: 2,
  StringSelect: 3,
  TextInput: 4,
  UserSelect: 5,
  RoleSelect: 6,
  MentionableSelect: 7,
  ChannelSelect: 8,
} as const

export const ButtonStyle = { Primary: 1, Secondary: 2, Success: 3, Danger: 4, Link: 5, Premium: 6 } as const

/** Эмодзи на кнопке или у варианта списка: id — кастомный, иначе name — символ Unicode. */
export interface ComponentEmoji {
  id?: Snowflake | null
  name?: string | null
  animated?: boolean
}

export interface ButtonComponent {
  type: 2
  style: number
  label?: string | null
  emoji?: ComponentEmoji | null
  custom_id?: string
  url?: string
  disabled?: boolean
}

export interface SelectOption {
  label: string
  value: string
  description?: string | null
  emoji?: ComponentEmoji | null
  default?: boolean
}

/** Список: 3 — свои варианты, 5 — участники, 6 — роли, 7 — участники и роли, 8 — каналы. */
export interface SelectComponent {
  type: 3 | 5 | 6 | 7 | 8
  custom_id: string
  options?: SelectOption[]
  channel_types?: number[]
  placeholder?: string | null
  min_values?: number
  max_values?: number
  disabled?: boolean
}

export interface ActionRow {
  type: 1
  components: (ButtonComponent | SelectComponent)[]
}

/** Кто и чем вызвал ответ приложения. */
export interface MessageInteractionMetadata {
  id: Snowflake
  type: number
  user: User
  authorizing_integration_owners?: Record<string, Snowflake>
  original_response_message_id?: Snowflake
  /** У ответа на кнопку или список: сообщение, на котором нажали. */
  interacted_message_id?: Snowflake
  /** Имя команды — Discord кладёт его и сюда. */
  name?: string
}

/** Старая форма interaction_metadata: name — полное имя команды с подкомандой («музыка играй»). */
export interface MessageInteraction {
  id: Snowflake
  type: number
  name: string
  user: User
}
